import moment from "moment-timezone";
import empty from "is-empty";
import { store } from "../index";
import {
    FETCH_STATES,
    TX_STATES,
    LOAN_TYPES,
    HUMANIZED_TX_STATES,
} from "../redux/Tx/constants";
import { COUNTRY_OPTIONS } from "../redux/MarketData/constants";
import { ROLE } from "../redux/TopBar/constants";
import { toUiCollType } from "src/utils/converting";
import {
    isCountryToCheckQty,
    getTermFromDate,
    EMAIL_REGEX,
    getMaximumPossibleMaturityDate,
    getClampedMaturityDate,
} from "./index";
import {
    convertRate,
    convertEntryByEconomics,
    getListSettingData,
} from "./converting";
import {
    DEFAULT_CCY_OPTIONS,
    TX_PURPOSE_LABELS,
} from "../redux/Confirmations/constants";
import { ERROR_TYPE } from "../components/common/AgGrid/TruTooltip/constants";
import { TxSelectors } from "../redux/Tx/selectors";
import { CounterPartysSelectors } from "../redux/CounterPartys/selectors";
import { getValuesFromNode } from "./ag-grid";
import { isNumber } from "mathjs";
import { SettingsSelectors } from "../redux/Settings/selectors";
import { InventorySelectors } from "../redux/Inventory/selectors";
import {
    HUMANIZED_INTERMEDIARY,
    HUMANIZED_TX_TYPES,
} from "../redux/Confirmations/constants";
import { RECALL_STATES } from "../redux/Recall/constants";
import * as Validators from "src/utils/validators";

export const TRU_MAX_QUANTITY = 1000000000;
export const TRU_MAX_AMOUNT = 1000000000000000;

const isEmptyNumber = (val) => empty(val) && val !== 0;

export const isMarketDataValid = (marketData, isRemovedConstituent = false) => {
    return marketData?.status !== FETCH_STATES.ERROR || isRemovedConstituent;
};

export const isAllowedLenderSecurity = (isLender, marketData) => {
    if (!isLender) return true;

    if (!marketData) return true; // handled by other popup

    if (
        marketData.status === FETCH_STATES.ERROR ||
        marketData.status === FETCH_STATES.FETCHING
    )
        return true; // handled by other popup

    const { "enable-lender-block-securities": enableLenderBlockSecurities } =
        SettingsSelectors.FeatureFlags(store.getState());

    if (!enableLenderBlockSecurities) return true;

    const currentBlockedSecurities =
        SettingsSelectors.LenderBlockedSecuritiesSettings(store.getState());

    const blockedSecurityIDs = new Set(
        currentBlockedSecurities.map((e) => e.securityId)
    );

    if (blockedSecurityIDs.has(marketData.data.id)) return false;

    return true;
};

export const isMarketDataFetched = (
    marketData,
    isRemovedConstituent = false
) => {
    return marketData?.status === FETCH_STATES.FETCHED || isRemovedConstituent;
};

// Market data settles once the lookup returns, either way. A pasted row
// carries no marketData at all until then, so "not settled" covers both the
// gap before the request goes out and the request itself being in flight.
export const isMarketDataSettled = (marketData) => {
    return (
        marketData?.status === FETCH_STATES.FETCHED ||
        marketData?.status === FETCH_STATES.ERROR
    );
};
export const isCheckToThousand = (country, value) => {
    return !isCountryToCheckQty(country) || (value / 1000) % 1 === 0;
};

export const isQuantityValid = (
    quantity,
    country,
    editable = true,
    allowZero = false
) => {
    const value = parseInt(quantity);
    if (value === 0 && allowZero) return true;
    if (isNaN(value) || (editable && value <= 0) || value > TRU_MAX_QUANTITY)
        return false;
    return isCheckToThousand(country, value);
};

export const isDynamicOrClientPoolList = (listName) => {
    if (empty(listName)) return false;

    const dynamicOrClientPoolLists = SettingsSelectors.DynamicAndClientPoolList(
        store.getState()
    );
    return !!dynamicOrClientPoolLists.find((list) => list.name === listName);
};

export const isSelectedRowsTxQuantityBelowInventoryQty = (
    state,
    listName,
    isDynamicOrClientPoolList,
    securityId,
    role
) => {
    if (role !== "LENDER" && role !== ROLE.LENDER) return true; // only checking on lender side
    let totalOffered = TxSelectors.SelectedTxTotalOfferedQty(
        state,
        listName,
        securityId
    );
    return isTxQuantityBelowInventoryQty(
        listName,
        isDynamicOrClientPoolList,
        totalOffered,
        securityId,
        role
    );
};

export const isCreateRowsTxQuantityBelowInventoryQty = (
    state,
    listName,
    isDynamicOrClientPoolList,
    securityId,
    isin,
    role
) => {
    if (role !== "LENDER" && role !== ROLE.LENDER) return true; // only checking on lender side
    let totalOffered = TxSelectors.CreateRowDataTxTotalOfferedQty(
        store.getState(),
        listName,
        isin
    ); // bug: rows store ISIN in SecurityId column
    return isTxQuantityBelowInventoryQty(
        listName,
        isDynamicOrClientPoolList,
        totalOffered,
        securityId,
        role
    );
};

export const isTxQuantityBelowInventoryQty = (
    listName,
    isDynamicOrClientPoolList,
    offerQty,
    securityId,
    role
) => {
    if (role !== "LENDER" && role !== ROLE.LENDER) return true; // only checking on lender side

    if (!securityId) return true; // sometimes market data is not fully loaded before this check is made..

    if (empty(listName)) return true; // no list set

    // no inventory left for dynamic list. Normally dynamic lists are deleted
    // after all associated inventory is cleared, but due to eventual consistency
    // there may temporarily be some leftover lists still visible.
    const inventoryList = InventorySelectors.GetInventoryList(
        store.getState()
    ).filter(
        (inv) =>
            inv.ListName === listName &&
            inv.SecurityId.split("#")[0] === securityId.split("#")[0]
    );
    if (empty(inventoryList)) return !isDynamicOrClientPoolList; // for non-dynamic lists it's ok if there is missing inventory

    const inventoryQty = inventoryList
        .map((inv) => inv.TruQuantity)
        .reduce((a, b) => a + b, 0);
    return offerQty <= inventoryQty;
};

export const isPartnerTradeQuantityValid = (
    role,
    reqQty,
    offerQty,
    minQty,
    incQty,
    enableMinIncrementQtyChecks,
    isPartnerTrade,
    status
) => {
    if (
        enableMinIncrementQtyChecks &&
        role === "LENDER" &&
        isPartnerTrade === true &&
        (status === TX_STATES.OFFERING ||
            status === TX_STATES.PRE_LOAN_REQUESTED)
    ) {
        // exceeds below minimum
        if (minQty !== undefined && offerQty < minQty) {
            return false;
        }

        // exceeds above requested (a.k.a. maximum)
        if (reqQty !== undefined && offerQty > reqQty) {
            return false;
        }

        // is not a multiple of increment quantity
        if (incQty !== undefined && offerQty % incQty !== 0) {
            return false;
        }
    }

    return true;
};

export const isTxQuantityValid = (
    country,
    txType,
    role,
    reqQty,
    offerQty,
    minQty,
    incQty,
    enableMinIncrementQtyChecks,
    isPartnerTrade,
    status,
    // Spec §2.1.3.3 / §2.2.2.3 — under limited-ui the lender's offer
    // qty may only be adjusted DOWN from the borrower's requested
    // qty (equal is fine). Required, not defaulted: a silent default
    // would let callers forget to thread the flag and the cell /
    // row / submit-button validators would silently disagree.
    limitedUi
) => {
    if (
        !isPartnerTradeQuantityValid(
            role,
            reqQty,
            offerQty,
            minQty,
            incQty,
            enableMinIncrementQtyChecks,
            isPartnerTrade,
            status
        )
    ) {
        return false;
    }

    if (role === "LENDER") {
        if (txType === LOAN_TYPES.HOLD)
            return isQuantityValid(offerQty, country) && offerQty <= reqQty;

        if (
            limitedUi &&
            isQuantityValid(reqQty, country) &&
            offerQty > reqQty
        ) {
            return false;
        }

        return isQuantityValid(offerQty, country);
    }
    if (!isQuantityValid(reqQty, country)) return false;
    if (txType !== LOAN_TYPES.HOLD) return true;

    return reqQty <= offerQty;
};

export const isAmountValid = (quantity, country) => {
    const value = parseInt(quantity);
    if (isNaN(value) || value <= 0 || value > TRU_MAX_AMOUNT) return false;
    return isCheckToThousand(country, value);
};

export const qtyErrorType = (quantity, country) => {
    const value = parseInt(quantity);
    if (isNaN(value) || value <= 0 || value > TRU_MAX_QUANTITY)
        return ERROR_TYPE.QTY;
    return isCountryToCheckQty(country) && (value / 1000) % 1 !== 0
        ? ERROR_TYPE.QTY_TW
        : null;
};

export const isOfferQuantityValid = (
    reqQuantity,
    quantity,
    country,
    type,
    status
) => {
    if (type === "HOLD" && (status === "CONFIRMED" || status === "FORKED"))
        return true;
    return (
        isQuantityValid(quantity, country) &&
        (!reqQuantity || quantity <= reqQuantity)
    );
};

export const checkQuantityWithOffer = (params) => {
    if (params.data) return false;
    if (!params.node?.aggData) return false;
    const { ticker, borrower, lender } = getValuesFromNode(params);
    if (!ticker || !borrower || !lender) return false;

    const txEntries = TxSelectors.TxEntries(store.getState());
    const nodes = txEntries.filter(
        (e) =>
            e.Ticker === ticker &&
            e.Borrower === borrower &&
            e.Lender === lender
    );

    const offeredList = nodes.filter(
        (n) => n.Status !== TX_STATES.PRE_LOAN_REQUESTED
    );

    const totalReqQty = nodes
        .filter((n) => n.Status === TX_STATES.PRE_LOAN_REQUESTED)
        .reduce(
            (total, node) =>
                total + (isNumber(node.ReqQuantity) ? node.ReqQuantity : 0),
            0
        );

    const totalOffered = offeredList.reduce(
        (total, node) =>
            total + (isNumber(node.OfferQuantity) ? node.OfferQuantity : 0),
        0
    );

    return totalReqQty > totalOffered;
};

export const isRateValid = (rate) => {
    const value = parseFloat(rate);

    return !isNaN(value) && value <= 500 && value >= -500;
};

export const isTxRateValid = (
    txType,
    role,
    reqRate,
    offerRate,
    // Spec §2.1.3.2 / §2.2.2.2 — under limited-ui, when the borrower
    // specified a reqRate the lender's offer may only be adjusted
    // DOWN (offerRate ≤ reqRate). If the borrower did NOT specify a
    // rate, the lender can set any value. Required, not defaulted:
    // see isTxQuantityValid above for the same reasoning.
    limitedUi
) => {
    if (role === "LENDER") {
        if (limitedUi && isRateValid(reqRate) && !(offerRate <= reqRate)) {
            return false;
        }
        return isRateValid(offerRate);
    }
    if (!isRateValid(reqRate)) return false;
    if (txType !== LOAN_TYPES.HOLD) return true;
    return reqRate >= offerRate;
};

export const isCallableValid = (callable) => {
    return typeof callable === "boolean";
};

export const isDividendValid = (div) => {
    let value = parseFloat(div);

    return !isNaN(value) && 0 <= value && value <= 100;
};

export const isPartnerRateCompatible = (
    collateralType,
    requestRate,
    offeredRate
) => {
    if (requestRate !== undefined && !isRateValid(requestRate)) return false;
    if (!isRateValid(offeredRate)) return false;

    if (requestRate === undefined) return true; // accept any

    if (collateralType === "NC" || collateralType === "CP") {
        if (offeredRate > requestRate) {
            return false;
        }
    } else if (collateralType === "CA") {
        if (offeredRate < requestRate) {
            return false;
        }
    }

    return true;
};

export const isDividendCompatible = (
    dividendIndicator,
    requestDividend,
    offerDividend
) => {
    if (requestDividend === undefined) return true;

    if (dividendIndicator === true) {
        if (offerDividend > requestDividend) {
            return false;
        }
    } else {
        if (offerDividend !== requestDividend) {
            return false;
        }
    }

    return true;
};

export const isFRValid = (fr) => {
    return !fr || ["", "F", "R"].includes(fr);
};

export const isCollTypeValid = (collType) => {
    return !collType || ["", "CA", "NC", "CP"].includes(collType);
};

export const isOfferCollTypeCompatibleWithRequestCollType = (
    isPartnerTrade,
    uiOfferCollType,
    uiReqCollType
) => {
    if (!isPartnerTrade) return true;

    if (!uiOfferCollType || uiOfferCollType === "" || uiOfferCollType === "Any")
        return true; // assume requested collateral is taken

    if (!uiReqCollType || uiReqCollType === "" || uiReqCollType === "Any")
        return true; // assume offered collateral is taken

    if (uiOfferCollType !== uiReqCollType) return false;

    return true;
};

export const isOfferCollCcyCompatibleWithRequestCollCcy = (
    isPartnerTrade,
    uiOfferCcy,
    uiReqCcy
) => {
    if (!isPartnerTrade) return true;

    if (!uiOfferCcy || uiOfferCcy === "") return true; // assume requested collateral is taken

    if (!uiReqCcy || uiReqCcy === "") return true; // assume offered collateral is taken

    if (uiOfferCcy !== uiReqCcy) return false;

    return true;
};

export const isCollCcyValid = (collCcy, selectableCCy) => {
    if (selectableCCy) return !empty(collCcy); // one of them must be selected
    return !collCcy || DEFAULT_CCY_OPTIONS.includes(collCcy);
};

export const isUtilizationValid = (util) => {
    return !isNaN(util) && util >= 0 && util <= 100;
};

export const isDateValid = (
    dateunformatted,
    tradeableDates,
    allowPastDates = false
) => {
    // TODO: decide if no value date is allowed in general
    if (dateunformatted && tradeableDates) {
        const date = convertDateToStr(dateunformatted.toString()).replace(
            /\//g,
            ""
        );

        const isPastOrToday = moment(date).isSameOrBefore(
            moment().startOf("day")
        );
        if (allowPastDates && isPastOrToday) return true;

        const isPastDate = moment(date).isBefore(moment().startOf("day"));

        // console.log("isDateValid called with "+date+", "+JSON.stringify(tradeableDates));
        return !isPastDate && tradeableDates.includes(date);
    }

    // so that row or cell error does not show while market data is loading
    // NOTE:
    // - !undefined = true
    // - ![] = false
    return !tradeableDates;
};

export const isDateTimeValid = (dateTime) => {
    if (!dateTime) return false;
    return moment(dateTime).isValid();
};

// TODO: Don't validate the term here, validate the maturity date..
export const isTermValid = (term) => {
    return !term || (Number.isInteger(term) && term > 0);
};

export const isSettlementDateValid = (dDate, sDate) => {
    if (!dDate && !sDate) return true;
    if (!dDate || !sDate) return !sDate;
    return moment(sDate).isSameOrBefore(moment(dDate));
};

export const isMaxMaturityValid = (data, enable90DayMaxMaturity) => {
    const maxMaturityDate = getMaximumPossibleMaturityDate(
        data.Country,
        data.Purpose,
        data.ValueDate,
        data.TradeableDates,
        data.maxMaturityDates,
        enable90DayMaxMaturity
    );

    if (
        maxMaturityDate === undefined || // any allowed
        data.Maturity === undefined ||
        data.Maturity === null
    )
        return true;

    const maxMaturityAsDate = new Date(maxMaturityDate); // it's YYYY-MM-DD as a string

    // FYI: annoyingly sometimes this is a string, other times a date.
    const maturityAsDate = new Date(data.Maturity);

    const isValid = maturityAsDate <= maxMaturityAsDate;

    return isValid;
};

export const isMaxQtyValid = (maxQty) => {
    const value = parseInt(maxQty);

    return (
        !isNaN(value) &&
        0 <= value &&
        value <= 100 &&
        precision(parseFloat(maxQty)) === 0
    );
};

export const isKsdAccountNoValid = (code) => {
    if (isEmptyNumber(code)) return true;
    return code.length === 10 && /\d{10}/g.test(code);
};

export const isKsfcAccountNoValid = (code) => {
    if (isEmptyNumber(code)) return true;
    return code.length === 12 && /\d{12}/g.test(code);
};

export const isSlbAccountNoValid = (code) => {
    if (isEmptyNumber(code)) return true;
    return code.length === 2 && /\d{2}/g.test(code);
};

export const isMaxRateValid = (maxRate) => {
    const value = parseFloat(maxRate);

    return !isNaN(value) && 0 <= value && value <= 100 && precision(value) <= 2;
};

export const isMaxUtilizationValid = (maxUtil) => {
    const value = parseFloat(maxUtil);

    return !isNaN(value) && 0 <= value && value <= 100 && precision(value) <= 1;
};

export const isMinValueValid = (minValue) => {
    const value = parseInt(minValue);

    return (
        !isNaN(value) &&
        0 <= value &&
        value <= TRU_MAX_AMOUNT &&
        precision(parseFloat(minValue)) === 0
    );
};

export const isDivLiabilityValid = (div) => {
    const value = parseFloat(div);
    return !isNaN(value) && 0 <= value && value <= 100 && precision(value) <= 2;
};

export const isMultiplierValid = (multiplier) => {
    const value = parseFloat(multiplier);
    return !isNaN(value) && 0 < value && value <= 100 && precision(value) <= 2;
};

// To count decimal places without error (from stackoverflow)
// replace all float calculations with something more reliable
// eg) 1.1 * 100 produces 1.10.0000000001
export const precision = (a) => {
    if (!isFinite(a)) return 0;
    let e = 1,
        p = 0;
    while (Math.round(a * e) / e !== a) {
        e *= 10;
        p++;
    }
    return p;
};

export const isCancellable = (valueDate, country) => {
    // get local start of day
    const marketHours = COUNTRY_OPTIONS[country];
    const utc = moment.utc();

    const VD = moment.utc(valueDate).startOf("day");

    const localDate = marketHours
        ? utc.tz(marketHours.zone).startOf("day")
        : utc.startOf("day");

    return !VD.isBefore(localDate);
};

export const isTxForkable = (role, txType, txStatus) => {
    return (
        role === ROLE.LENDER &&
        txType === LOAN_TYPES.HOLD &&
        txStatus === TX_STATES.CONFIRMED
    );
};

export const verifyAcceptanceCriteria = (includesAC, ac, createRowData) => {
    if (!includesAC) return { errors: [], disables: [] };
    const errors = [];
    const disables = [];

    // if 'minQuantityPercent' is '' (blank), number will be 0
    if (empty(ac.minQuantityPercent)) errors.push("minQuantityPercent");
    else {
        const percent = Number(ac.minQuantityPercent);
        if (!(percent > 0 && percent <= 100)) errors.push("minQuantityPercent");
    }

    // maxRateBps
    if (
        createRowData.filter((d) => d.Rate !== null && d.Rate !== undefined)
            .length > 0
    ) {
        if (ac.maxRateBps !== 0 && empty(ac.maxRateBps))
            errors.push("maxRateBps");
        else {
            const bps = Number(ac.maxRateBps);
            if (!(bps >= 0 && bps <= 10000)) errors.push("maxRateBps");
        }
    } else {
        disables.push("maxRateBps");
    }

    // maxDiv
    const div = Number(ac.maxDiv);
    if (!(empty(ac.maxDiv) || (div >= 0 && div <= 100))) errors.push("maxDiv");
    // TODO: verify another fields here

    return { errors, disables };
};

export const verifyOfferSettings = (offerSettings) => {
    return [];
};

// input: date string like 'Mon Aug 24 2020 00:00:00 GMT+0300'
// output: date string ex) '2020/10/10'
export const convertDateToStr = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY/MM/DD"); // get only picked date. ignore timezone of calendar
};

export const getConfirmFieldErrorType = (value, changes) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        SettingsSelectors.FeatureFlags(store.getState());

    const { rowData, fieldName, newValue } = value;
    switch (fieldName) {
        case "quantity":
            const errorType = qtyErrorType(newValue, rowData.security_country);
            if (errorType) return errorType;
            if (
                !empty(changes[rowData.confirmation_id]) &&
                !empty(changes[rowData.confirmation_id].originValue.quantity)
            ) {
                return newValue <=
                    parseInt(
                        changes[rowData.confirmation_id].originValue.quantity
                    )
                    ? null
                    : ERROR_TYPE.QTY_COMPARE_CONFIRM;
            }
            return null;
        case "rate":
            return isRateValid(newValue) ? null : ERROR_TYPE.RATE;
        case "value_date":
            return empty(newValue) ? ERROR_TYPE.VALUE_DATE : null;
        case "maturity_date":
            return isTermValid(getTermFromDate(rowData.value_date, newValue))
                ? isMaxMaturityValid(rowData, enable90DayMaxMaturity)
                    ? null
                    : ERROR_TYPE.MATURITY_MAX_EXCEED
                : ERROR_TYPE.MATURITY;
        case "dividend":
            return isDividendValid(newValue) ? null : ERROR_TYPE.DIV;
        default:
            return null;
    }
};

export const isTxInitRowValid = (row, role) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        SettingsSelectors.FeatureFlags(store.getState());

    return role === ROLE.LENDER
        ? isMarketDataValid(row.marketData) &&
              isQuantityValid(row.Quantity, row.Country) &&
              isRateValid(row.CalculatedRate) &&
              isDateValid(row.ValueDate, row.TradeableDates) &&
              isTermValid(row.Term) &&
              // isMaxMaturityValid(row, enable90DayMaxMaturity) &&  // we only need to validate on the borrower side
              isListNameValid(row.ListName) &&
              isDivLiabilityValid(row.Dividend) &&
              isAllowedLenderSecurity(true, row.marketData) &&
              // for dynamic lists validate offer qty does not exceed tru qty
              (!Validators.isDynamicOrClientPoolList(row.ListName) ||
                  Validators.isCreateRowsTxQuantityBelowInventoryQty(
                      store.getState(),
                      row.ListName,
                      true,
                      row.marketData?.data?.id,
                      row.SecurityId,
                      "LENDER"
                  ))
        : isMarketDataValid(row.marketData, row.isRemoved) &&
              isQuantityValid(row.Quantity, row.Country) &&
              (!row.CalculatedRate || isRateValid(row.CalculatedRate)) &&
              (!row.ValueDate ||
                  isDateValid(row.ValueDate, row.TradeableDates)) &&
              isTermValid(row.Term) &&
              // only validate maturity if it's set, otherwise any is allowed until the offer is accepted
              // at which point it will be clamped to 90 days
              (row.Maturity === undefined ||
                  row.Maturity === "" ||
                  isMaxMaturityValid(row, enable90DayMaxMaturity));
};

export const isTxListRowValid = (entry) => {
    if (!entry) return true;
    const selectedRowIds = TxSelectors.SelectedRowIds(store.getState());

    if (entry.isRemovedConstituent === true) return false;
    if (entry.isRemovedConstituent === false) return true;

    if (!selectedRowIds.includes(entry.id)) return true;
    const {
        ValueDate,
        Dividend,
        TradeableDates,
        CollateralType,
        CollateralCCY,
        SelectableCCY,
        Status,
        ListName,
        Term,
        Country,
        Type,
        MyRole,
        OfferQuantity,
        OfferRate,
        Intermediary,
        TxType,
        ReqQuantity,
        MinQuantity,
        IncQuantity,
        ReqRate,
        SecurityId,
        IsPartnerTrade,
        isPortfolioTrade,
        OriginValue,
        Callable,
    } = entry;

    const {
        "enable-min-increment-quantity": enableMinIncrementQty,
        "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
        // Spec §2.1.3.2 / §2.1.3.3 — pulled here and passed into the
        // qty + rate validators below so this row-level (orange)
        // validator reports the same verdict the cell-level (red) and
        // submit-button validators do under limited-ui.
        "limited-ui": limitedUi,
    } = SettingsSelectors.FeatureFlags(store.getState());
    if (Status === TX_STATES.OFFERED) {
        return true;
    }

    // note: complexity due to the row which is about to be updated doesn't include the new
    // OfferQuantity in its 'offeredEconomics.quantity', so we have to sum it separately
    if (
        MyRole === "LENDER" &&
        !empty(ListName) &&
        isDynamicOrClientPoolList(ListName)
    ) {
        let totalOffered =
            OfferQuantity +
            TxSelectors.TxRawEntries(store.getState())
                .filter(
                    (e) =>
                        e.id !== entry.id && // see above
                        selectedRowIds.includes(e.id) &&
                        e.listName === ListName &&
                        e.security?.id === SecurityId
                )
                .map((e) => e.offeredEconomics.quantity)
                .reduce((a, b) => a + b, 0);
        if (
            !isTxQuantityBelowInventoryQty(
                ListName,
                true,
                totalOffered,
                SecurityId,
                MyRole
            )
        )
            return false;
    }

    // edge-case: qty of entry may be 0 or -, but total for split offers might be sufficient.
    // in this case we still must reject this entry.
    let isEntryQtyValid = isQuantityValid(OfferQuantity, Country);

    let ParentId = entry.ParentId;
    let offerQuantity =
        ParentId === undefined
            ? OfferQuantity
            : TxSelectors.TotalOfferForPreloanId(store.getState(), ParentId);

    let isTotalQtyValid = isTxQuantityValid(
        Country,
        Type,
        MyRole,
        ReqQuantity,
        offerQuantity,
        MinQuantity,
        IncQuantity,
        enableMinIncrementQty,
        IsPartnerTrade,
        Status,
        limitedUi
    );

    let isValueDateValidForPartnerTrade =
        !IsPartnerTrade ||
        entry.OriginValue.ValueDate === undefined ||
        ValueDate === entry.OriginValue.ValueDate;

    let MaturityDate = entry.Maturity;

    let isMaturityDateValidForPartnerTrade =
        !IsPartnerTrade ||
        (MaturityDate || "9999-12-31") ===
            (entry.OriginValue.Maturity || "9999-12-31");

    if (isPortfolioTrade && offerQuantity > ReqQuantity) return false;

    return (
        isEntryQtyValid &&
        isTotalQtyValid &&
        (IsPartnerTrade ||
            isTxRateValid(Type, MyRole, ReqRate, OfferRate, limitedUi)) &&
        (!IsPartnerTrade ||
            isPartnerRateCompatible(CollateralType, ReqRate, OfferRate)) &&
        isDateValid(ValueDate, TradeableDates) &&
        isValueDateValidForPartnerTrade &&
        isDividendValid(Dividend) &&
        (!IsPartnerTrade ||
            isDividendCompatible(
                OriginValue.DividendIndicator,
                OriginValue.Dividend,
                Dividend
            )) &&
        (!IsPartnerTrade || Callable === OriginValue.Callable) &&
        isCollCcyValid(CollateralCCY, SelectableCCY) &&
        isCollTypeValid(CollateralType) &&
        isOfferCollTypeCompatibleWithRequestCollType(
            IsPartnerTrade,
            CollateralType,
            entry.OriginValue.CollateralType
        ) &&
        isOfferCollCcyCompatibleWithRequestCollCcy(
            IsPartnerTrade,
            CollateralCCY,
            entry.OriginValue.CollateralCCY
        ) &&
        isCompatibleIntermediary(TxType, Intermediary) &&
        (Status === TX_STATES.PRE_LOAN_REQUESTED ||
        Status === TX_STATES.OFFERING
            ? isListNameValid(ListName)
            : true) &&
        isTermValid(Term) &&
        isMaturityDateValidForPartnerTrade &&
        isMaxMaturityValid(entry, enable90DayMaxMaturity)
    );
};

export const isTxDataRowValid = (
    entry,
    {
        enabledMinIncrementQty,
        enabled90DayMaxMaturity,
        selectedTxRows,
        getTotalOfferForPreloan,
        getIsPartnerTrade,
        // Spec §2.1.3.2 / §2.1.3.3 — threaded through to
        // isTxQuantityValid + isTxRateValid below so the submit-
        // button gate (this validator) agrees with the cell-level
        // (red) and row-level (orange) validators under limited-ui.
        // Required, not defaulted: see isTxQuantityValid for
        // reasoning.
        limitedUi,
    }
) => {
    if (entry.state === TX_STATES.OFFERED) {
        // borrower side
        return isFundNameValid(entry.fundName);
    }
    const Status = entry.state;
    const OfferRate = convertRate(entry.offeredEconomics);
    const ReqRate = convertRate(entry.requestedEconomics);
    const {
        CollateralType,
        CollateralCCY,
        SelectableCCY,
        ValueDate,
        Intermediary,
        TxType,
        Term,
    } = convertEntryByEconomics(entry);
    const Dividend = entry.offeredEconomics?.dividend;
    const Callable = entry.offeredEconomics?.callable;
    const TradeableDates = entry.security?.tradableDates?.map((s) =>
        s.replace(/-/g, "")
    );
    const MyRole = entry.myRole;
    const Type = entry.type;
    const ListName = entry.listName;
    const Country = entry.security?.country || "KR";
    const OfferQuantity = entry.offeredEconomics?.quantity;
    const ReqQuantity = entry.requestedEconomics?.quantity;
    const ReqDividendIndicator = entry.requestedEconomics?.dividendIndicator;
    const ReqDividend = entry.requestedEconomics?.dividend;
    const MinQuantity = entry.requestedEconomics?.minQuantity;
    const IncQuantity = entry.requestedEconomics?.incQuantity;
    const ReqCollateralType = entry.requestedEconomics?.collateralType?.type;
    const ReqCollateralCcy = entry.requestedEconomics?.collateralType?.ccy?.[0];
    const ReqValueDate = entry.requestedEconomics?.valueDate;
    const ReqTerm = entry.requestedEconomics?.maturityDate;
    const ReqCallable = entry.requestedEconomics?.callable;

    if (
        MyRole === "LENDER" &&
        !empty(ListName) &&
        isDynamicOrClientPoolList(ListName)
    ) {
        let totalOffered = selectedTxRows
            .filter(
                // NOTE: list name isn't set here yet when it's selected for the first time,
                // but we probably don't need this check anyway
                // e.listName === ListName &&
                (e) => e.security?.id === entry.security?.id
            )
            .map((e) => e.offeredEconomics.quantity)
            .reduce((a, b) => a + b, 0);
        if (
            !isTxQuantityBelowInventoryQty(
                ListName,
                true,
                totalOffered,
                entry.security?.id,
                MyRole
            )
        )
            return false;
    }

    let IsPartnerTrade = getIsPartnerTrade(entry.borrower?.id);

    // edge-case: qty of entry may be 0 or -, but total for split offers might be sufficient.
    // in this case we still must reject this entry.
    let isEntryQtyValid = isQuantityValid(OfferQuantity, Country);

    let ParentId = entry.preloanId;
    let offerQuantity =
        ParentId === undefined
            ? OfferQuantity
            : getTotalOfferForPreloan(ParentId);

    let isTotalQtyValid = isTxQuantityValid(
        Country,
        Type,
        MyRole,
        ReqQuantity,
        offerQuantity,
        MinQuantity,
        IncQuantity,
        enabledMinIncrementQty,
        IsPartnerTrade,
        Status,
        limitedUi
    );

    const reqCollType = toUiCollType({ type: ReqCollateralType }).collType;

    let isValueDateValidForPartnerTrade =
        !IsPartnerTrade ||
        ReqValueDate === undefined ||
        ValueDate === ReqValueDate;

    let MaturityDate = entry.offeredEconomics?.maturityDate;

    let isMaturityDateValidForPartnerTrade =
        !IsPartnerTrade ||
        (MaturityDate || "9999-12-31") === (ReqTerm || "9999-12-31");

    if (entry.portfolioTrade === true && offerQuantity > ReqQuantity)
        return false;

    return (
        isEntryQtyValid &&
        isTotalQtyValid &&
        (IsPartnerTrade ||
            isTxRateValid(Type, MyRole, ReqRate, OfferRate, limitedUi)) &&
        (!IsPartnerTrade ||
            isPartnerRateCompatible(CollateralType, ReqRate, OfferRate)) &&
        isDateValid(ValueDate, TradeableDates) &&
        isValueDateValidForPartnerTrade &&
        isDividendValid(Dividend) &&
        (!IsPartnerTrade ||
            isDividendCompatible(
                ReqDividendIndicator,
                ReqDividend,
                Dividend
            )) &&
        (!IsPartnerTrade || Callable === ReqCallable) &&
        isCollCcyValid(CollateralCCY, SelectableCCY) &&
        isCollTypeValid(CollateralType) &&
        isOfferCollTypeCompatibleWithRequestCollType(
            IsPartnerTrade,
            CollateralType,
            reqCollType
        ) &&
        isOfferCollCcyCompatibleWithRequestCollCcy(
            IsPartnerTrade,
            CollateralCCY,
            ReqCollateralCcy
        ) &&
        isCompatibleIntermediary(TxType, Intermediary) &&
        (Status === TX_STATES.PRE_LOAN_REQUESTED ||
        Status === TX_STATES.OFFERING
            ? isListNameValid(ListName)
            : true) &&
        isTermValid(Term) &&
        isMaturityDateValidForPartnerTrade &&
        isMaxMaturityValid(entry, enabled90DayMaxMaturity)
    );
};

export const isFundNameValid = (fundName) => {
    if (empty(fundName)) return true; // may be empty
    const fundSettings = SettingsSelectors.SelectableFundNames(
        store.getState()
    );
    return fundSettings.includes(fundName);
};

export const isListNameValid = (listName) => {
    if (empty(listName)) return false;
    const listSettings = SettingsSelectors.SelectableListNames(
        store.getState()
    );
    return listSettings.includes(listName);
};

export const isInventoryListNameValid = (listName) => {
    if (empty(listName)) return false;
    const listSettings = SettingsSelectors.ListSettingsWithOrder(
        store.getState()
    );
    return listSettings.includes(listName);
};

export const isTxListRowInactive = (txType, txStatus, txQty) => {
    // note: pre-existing bug before https://github.com/TrueShort/truweb-ui/pull/753,
    // explicitly checking undefined as a workaround for now
    if (txType === undefined) return false;

    switch (txType) {
        case LOAN_TYPES.LOAN:
            if (!txStatus) return true;

            if (
                [
                    TX_STATES.CONFIRMED,
                    TX_STATES.REJECTED,
                    TX_STATES.EXPIRED,
                    TX_STATES.CANCELLED,
                    TX_STATES.WITHDRAWN,
                ].includes(txStatus)
            )
                return true;

            if (txStatus === TX_STATES.PRE_LOAN_REQUESTED && txQty === 0)
                return true;

            return false;

        case LOAN_TYPES.HOLD:
            if (
                [
                    TX_STATES.REJECTED,
                    TX_STATES.EXPIRED,
                    TX_STATES.CANCELLED,
                    TX_STATES.RELEASED,
                    TX_STATES.WITHDRAWN,
                ].includes(txStatus)
            )
                return true;

            return false;

        case LOAN_TYPES.BID:
            if (!txStatus || txStatus === TX_STATES.CONFIRMED) return true;

            return false;

        default:
            throw new Error("Unhandled loan type: " + txType);
    }
};

export const isRecallActive = (dueDate, country, status) => {
    const marketHours = COUNTRY_OPTIONS[country];

    const utc = moment.utc();
    let targetTime = moment.utc(dueDate).endOf("day");
    let currentTime = utc;

    if (marketHours) {
        targetTime = moment.tz(
            `${dueDate} ${marketHours.dueDateExpirationHour}:00`,
            "YYYY-MM-DD HH:mm",
            marketHours.zone
        );
        currentTime = moment().tz(marketHours.zone);
    }
    return (
        currentTime.isBefore(targetTime) && status !== RECALL_STATES.CANCELED
    );
};

export const isRecallCancelable = (dueDate, country, status) =>
    isRecallActive(dueDate, country, status);

export const isRecallDisputable = (dueDate, country, status) =>
    isRecallActive(dueDate, country, status) &&
    status !== RECALL_STATES.DISPUTED;

export const isInventoryRowValid = (row) => {
    return (
        isMarketDataFetched(row.marketData) &&
        isQuantityValid(row.MyQuantity, row.Country) &&
        isRateValid(row.CalculatedRate) &&
        isCallableValid(row.Callable) &&
        (!row.Dividend || isDividendValid(row.Dividend)) &&
        isFRValid(row.FR) &&
        isDateValid(row.ValueDate, row.TradeableDates) &&
        isInventoryListNameValid(row.ListName)
    );
};

export const isTakeRowValid = (row) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        SettingsSelectors.FeatureFlags(store.getState());

    return (
        isQuantityValid(row.Quantity, row.Country) &&
        (!row.Rate || isRateValid(row.Rate)) &&
        (!row.ValueDate || isDateValid(row.ValueDate, row.TradeableDates)) &&
        isTermValid(row.Term) &&
        (row.Maturity === undefined ||
            row.Maturity === "" ||
            isMaxMaturityValid(row, enable90DayMaxMaturity))
    );
};

export const isStoreRowValid = (row) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        SettingsSelectors.FeatureFlags(store.getState());

    return (
        isQuantityValid(row.Quantity, row.Country) &&
        isRateValid(row.Rate) &&
        (!row.Dividend || isDividendValid(row.Dividend)) &&
        !!row.ValueDate &&
        isDateValid(row.ValueDate, row.TradeableDates, true) &&
        isDateTimeValid(row.ConfirmedTime) &&
        isTermValid(row.Term) &&
        isMaxMaturityValid(row, enable90DayMaxMaturity)
    );
};

export const isRecallRequestRowValid = (row) => {
    return (
        isMarketDataFetched(row.marketData) &&
        isQuantityValid(row.qty, row.country) &&
        isDateValid(row.dueDate, row.TradeableDates) &&
        isSettlementDateValid(row.dueDate, row.settlementDate)
    );
};

const _prepareRowEdit = (rowData, colId, _clampMaturity) => {
    switch (colId) {
        case "SecurityId":
            return {
                ...rowData,
                marketData: {
                    status: FETCH_STATES.FETCHING,
                },
                Name: "",
                LocalName: "",
                PreviousClose: "",
                Amount: 0,
            };
        case "CalculatedRate":
            return {
                ...rowData,
                Rate: rowData.CalculatedRate,
                MyRate: rowData.CalculatedRate, //TODO: inventory uses MyRate , tx uses Rate ??
            };
        case "Quantity":
            const closingPrice =
                rowData.security?.closingPrice || rowData.PreviousClose;
            return {
                ...rowData,
                Amount: closingPrice ? closingPrice * rowData.Quantity : 0,
            };
        case "ListName":
            return getListSettingData(rowData, rowData.ListName);
        case "Maturity":
            return {
                ...rowData,
                Term: getTermFromDate(rowData.ValueDate, rowData.Maturity),
            };
        case "ValueDate":
            return {
                ...rowData,
                Maturity: rowData.ValueDate
                    ? rowData.Maturity
                    : new Date("9999-12-31"),
                Term: getTermFromDate(rowData.ValueDate, rowData.Maturity),
            };
        case "CollateralType":
            return {
                ...rowData,
                FR: rowData.CollateralType === "CA" ? "R" : "F",
            };
        default:
            return rowData;
    }
};

export const translateStatus = (state) => {
    return HUMANIZED_TX_STATES[state] || "-";
};

export const parseIntToSizeLimit = (value) => {
    let out = parseInt(value);
    if (!out) return value;
    if (out > 1000000000) {
        return value;
    }
    return out;
};

export const parseFloatToSizeLimit = (value) => {
    let out = parseFloat(value);
    if (!out) return value;
    if (out > 10000) {
        return value;
    }
    return out;
};

const fundCodeValidation = (newSetting, fields, isAccountSelectable) => {
    if (
        (!isAccountSelectable || newSetting.ksdAccount) &&
        empty(newSetting.ksdFundCode) &&
        empty(newSetting.slbNumber)
    ) {
        fields.push("noCodeExists");
        fields.push("slbNumber");
        fields.push("ksdFundCode");
    }

    if (!empty(newSetting.ksdFundCode) && newSetting.ksdFundCode.length > 12) {
        fields.push("ksdFundCodeCharacterLengthExceeded");
    }

    if (!empty(newSetting.slbNumber) && newSetting.slbNumber.length !== 2) {
        fields.push("slbNumberInvalid");
    }

    if (
        !empty(newSetting.ksfcFundCode) &&
        newSetting.ksfcFundCode.length > 12
    ) {
        fields.push("ksfcFundCodeCharacterLengthExceeded");
    }

    if (
        !empty(newSetting.internalFundCode) &&
        newSetting.internalFundCode.length > 12
    ) {
        fields.push("internalFundCodeCharacterLengthExceeded");
    }
};
export const getListValidationErrors = (
    newSetting,
    currentSettings,
    dynamicAndClientPoolList,
    isAccountSelectable
) => {
    const fields = [];

    if (empty(newSetting.name)) {
        fields.push("nameRequired");
    }
    if (newSetting?.name?.length > 50) {
        fields.push("nameMaxLengthExceeded");
    }
    if (
        currentSettings.some((setting) => setting.name === newSetting.name) ||
        dynamicAndClientPoolList.some(
            (setting) => setting.name === newSetting.name
        )
    ) {
        fields.push("nameExist");
    }

    fundCodeValidation(newSetting, fields, isAccountSelectable);

    if (
        !empty(newSetting.custodyAssetType) &&
        newSetting.custodyAssetType.length !== 2
    ) {
        fields.push("custodyAssetType");
    }

    if (!empty(newSetting.dividend) && !isDividendValid(newSetting.dividend)) {
        fields.push("dividend");
    }

    if (empty(newSetting.collCcy)) fields.push("collCcyRequired");

    return fields;
};

export const getFundValidationErrors = (
    newSetting,
    currentConf,
    isAccountSelectable
) => {
    const fields = [];
    if (empty(newSetting.fundName)) {
        fields.push("fundNameRequired");
    } else if (
        currentConf.some(
            (setting) => setting.fundName === newSetting.fundName.trim()
        )
    ) {
        fields.push("fundNameExist");
    } else if (newSetting?.fundName?.length > 50) {
        fields.push("fundNameMaxLengthExceeded");
    }

    fundCodeValidation(newSetting, fields, isAccountSelectable);

    return fields;
};

export const isEmailValid = (email) => EMAIL_REGEX.test(email);

export const INTERMEDIARY_SELECT_OPTIONS = (txType) => {
    return Object.keys(HUMANIZED_INTERMEDIARY).map((key) => ({
        label: HUMANIZED_INTERMEDIARY[key],
        value: key,
        disabled: !isCompatibleIntermediary(txType, key),
    }));
};

export const TX_TYPE_SELECT_OPTIONS = (intermediary) => {
    return Object.keys(HUMANIZED_TX_TYPES).map((key) => ({
        label: HUMANIZED_TX_TYPES[key],
        value: key,
        disabled: !isCompatibleIntermediary(key, intermediary),
    }));
};

export const isCompatibleIntermediary = (txType, intermediary) => {
    return COMPATIBLE_INTERMEDIARY_VALUES(txType).includes(intermediary);
};

export const COMPATIBLE_INTERMEDIARY_VALUES = (txType) => {
    switch (txType) {
        case "ANY":
        case "CUSTOMIZED":
            return ["ANY", "KSD", "KSFC", "KSDA"];
        case "ARRANGED":
        case "BID_OFFER":
            return ["ANY", "KSD", "KSFC"];
        case "FAIL_COVER":
            return ["ANY", "KSD"];
        default:
            return ["ANY", "KSD", "KSFC", "KSDA"];
    }
};

export const COMPATIBLE_TX_TYPE_VALUES = (intermediary) => {
    switch (intermediary) {
        case "ANY":
        case "KSD":
            return ["ANY", "CUSTOMIZED", "ARRANGED", "BID_OFFER", "FAIL_COVER"];
        case "KSFC":
            return ["ANY", "CUSTOMIZED", "ARRANGED", "BID_OFFER"];
        case "KSDA":
            return ["ANY", "CUSTOMIZED"];
        default:
            return ["ANY", "CUSTOMIZED", "ARRANGED", "BID_OFFER", "FAIL_COVER"];
    }
};

export const TX_PURPOSE_SELECT_OPTIONS = () => {
    return Object.values(TX_PURPOSE_LABELS).map((item) => item.value);
};

export const TX_PURPOSE_SELECT_LABELS = (value) => {
    return (
        Object.values(TX_PURPOSE_LABELS).find((item) => item.value === value)
            ?.label || "-"
    );
};

export const combinePrepareEdit = (...fns) => {
    return (...args) => {
        return fns.reduce((acc, f) => acc || f(...args), null);
    };
};

const prepareEditPurpose = (
    rowData,
    colId,
    featureFlags = {},
    clampMaturity
) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        featureFlags;

    if (colId === "Purpose" && enable90DayMaxMaturity) {
        let maturity;

        // reset to previous maturity date
        if (clampMaturity) {
            if (rowData.Purpose === "-") {
                maturity = rowData.OriginValue?.OriginOfferedMaturity;
            } else {
                maturity = getClampedMaturityDate(
                    rowData.Country,
                    rowData.Purpose,
                    rowData.Maturity,
                    rowData.ValueDate,
                    rowData.TradeableDates,
                    rowData.security.maxMaturityDates,
                    enable90DayMaxMaturity
                );
            }
        } else {
            maturity = rowData.Maturity;
        }

        return {
            ...rowData,
            Maturity: maturity,
            Term: getTermFromDate(rowData.ValueDate, maturity),
        };
    }
};

const prepareEditValueDate = (
    rowData,
    colId,
    featureFlags = {},
    _clampMaturity
) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        featureFlags;

    if (colId === "ValueDate" && enable90DayMaxMaturity) {
        const maturity = getClampedMaturityDate(
            rowData.Country,
            rowData.Purpose,
            rowData.Maturity,
            rowData.ValueDate,
            rowData.TradeableDates,
            rowData.maxMaturityDates,
            enable90DayMaxMaturity
        );

        return {
            ...rowData,
            Maturity: maturity,
            Term: getTermFromDate(rowData.ValueDate, maturity),
        };
    }
};

// The origin prepareRowEdit function stills remains the same if remove the prepareEditValueDate and prepareEditPurpose
export const prepareRowEdit = combinePrepareEdit(
    prepareEditPurpose,
    prepareEditValueDate,
    _prepareRowEdit
);
